<template>
	<TransitionRoot :show="visible" as="template">
		<Dialog as="div" class="relative z-50" @close="close">
			<TransitionChild
				as="template"
				enter="duration-300 ease-out"
				enter-from="opacity-0"
				enter-to="opacity-100"
				leave="duration-200 ease-in"
				leave-from="opacity-100"
				leave-to="opacity-0"
			>
				<div
					class="fixed bg-slate-950 bg-opacity-50 backdrop-blur-sm top-safe right-safe bottom-safe left-safe"
				/>
			</TransitionChild>
			<div class="fixed h-full top-safe right-safe bottom-safe left-safe">
				<div
					:class="[
						'flex h-full flex-col items-center justify-center text-center',
						panelContainerClass,
						pageMode ? 'p-0 lg:p-4' : 'p-4'
					]"
				>
					<TransitionChild
						as="template"
						enter="duration-300 ease-out"
						enter-from="opacity-0 scale-95"
						enter-to="opacity-100 scale-100"
						leave="duration-200 ease-in"
						leave-from="opacity-100 scale-100"
						leave-to="opacity-0 scale-95"
					>
						<DialogPanel
							:class="[
								'flex max-h-full w-full transform flex-col items-center justify-center overflow-hidden transition-all',
								panelClass,
								pageMode ? 'max-lg:h-full' : '',
								{
									[pageMode ? 'lg:max-w-sm' : 'max-w-sm']: size === 'sm',
									[pageMode ? 'lg:max-w-md' : 'max-w-md']: size === 'md',
									[pageMode ? 'lg:max-w-lg' : 'max-w-lg']: size === 'lg',
									[pageMode ? 'lg:max-w-xl' : 'max-w-xl']: size === 'xl',
									[pageMode ? 'lg:max-w-2xl' : 'max-w-2xl']: size === '2xl',
									[pageMode ? 'lg:max-w-3xl' : 'max-w-3xl']: size === '3xl',
									[pageMode ? 'lg:max-w-4xl' : 'max-w-4xl']: size === '4xl',
									[pageMode ? 'lg:max-w-[1024px]' : 'max-w-[1024px]']: size === '5xl',
									[pageMode ? 'lg:max-w-5xl' : 'max-w-6xl']: size === '6xl',
									[pageMode ? 'lg:max-w-5xl' : 'max-w-7xl']: size === '7xl'
								},
								pageMode
									? 'max-w-full'
									: {
											'max-w-sm': size === 'sm',
											'max-w-md': size === 'md',
											'max-w-lg': size === 'lg',
											'max-w-xl': size === 'xl',
											'max-w-2xl': size === '2xl',
											'max-w-3xl': size === '3xl',
											'max-w-4xl': size === '4xl',
											'max-w-5xl': size === '5xl',
											'max-w-6xl': size === '6xl',
											'max-w-7xl': size === '7xl',
											'w-full': size === 'full'
										}
							]"
						>
							<div
								:class="[
									'bg-white relative flex max-h-full w-full flex-col overflow-hidden text-start align-middle shadow-xl',
									cardClass,
									pageMode ? 'rounded-none max-lg:h-full lg:rounded-2xl' : 'rounded-2xl'
								]"
							>
								<DialogTitle
									v-if="!hideHeader"
									as="div"
									class="sticky z-[2] flex flex-shrink-0 flex-col top-safe"
								>
									<div class="border-gray-200 flex flex-shrink-0 justify-between border-b">
										<slot name="header">
											<h4 class="text-neutral-900 m-5 my-4 text-lg font-bold">
												{{ title }}
											</h4>
											<slot name="close">
												<div v-if="!hideClose" class="flex">
													<div
														class="ripple-bg-white border-gray-200 flex cursor-pointer items-center justify-center border-s p-3 rounded-te-2xl"
														aria-label="Close modal"
														@click="close"
													>
														<Icon name="mdi:close" class="h-8 w-8" />
													</div>
												</div>
											</slot>
										</slot>
									</div>
									<slot name="header-bottom">
										<div></div>
									</slot>
								</DialogTitle>
								<div :class="['styled-scrollbar flex-grow overflow-auto', bodyClass]">
									<slot></slot>
								</div>
								<slot
									v-if="!hideFooter && buttonName"
									name="footer"
									class="sticky flex-shrink-0 bottom-safe"
								>
									<div class="relative flex">
										<div class="bg-gray-200 absolute left-0 right-0 top-0 -mt-px h-px w-full" />
										<div class="flex-grow" />
										<button
											class="text-white m-4 rounded-xl bg-company px-8 py-3 font-bold duration-300 hover:bg-company-dark"
											:style="{
												background: companyStore.company?.color || undefined
											}"
											@click="click"
										>
											{{ buttonName }}
										</button>
									</div>
								</slot>
								<div
									v-if="loading"
									class="bg-white absolute bottom-0 left-0 right-0 top-0 z-[1] flex items-center justify-center rounded-2xl bg-opacity-50"
								>
									<div
										class="h-8 w-8 animate-ping rounded-full bg-company"
										:style="{
											background: companyStore.company?.color || undefined
										}"
									/>
								</div>
								<div
									v-if="!fetched && !loading"
									class="bg-white absolute bottom-0 left-0 right-0 top-0 z-[1] flex items-center justify-center rounded-2xl bg-opacity-50"
								>
									<span
										class="ripple-bg-amber-500 text-white cursor-pointer rounded-md px-4 py-2.5 text-sm font-bold shadow-lg"
										@click="emit('tryAgain')"
									>
										{{ $t("try_again") }}
									</span>
								</div>
							</div>
							<slot name="outside-bottom"></slot>
						</DialogPanel>
					</TransitionChild>
				</div>
			</div>
		</Dialog>
	</TransitionRoot>
</template>

<script setup lang="ts">
import { TransitionRoot, TransitionChild, Dialog, DialogPanel, DialogTitle } from "@headlessui/vue"

const app = useNuxtApp()
const companyStore = useCompanyStore()

defineSlots<{
	default: any
	header: any
	"header-bottom": any
	footer: any
	"outside-bottom": any
	close: any
}>()

const emit = defineEmits<{
	close: []
	"button-click": []
	tryAgain: []
}>()

const props = withDefaults(
	defineProps<{
		visible?: boolean
		title?: string
		buttonName?: string
		size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "7xl" | "full"
		loading?: boolean
		fetched?: boolean
		pageMode?: boolean
		hideHeader?: boolean
		hideFooter?: boolean
		hideClose?: boolean
		error?: Error | null
		panelClass?: unknown
		cardClass?: unknown
		panelContainerClass?: unknown
		bodyClass?: unknown
	}>(),
	{
		fetched: true,
		visible: false
	}
)

const close = () => {
	emit("close")
}

const click = () => {
	emit("button-click")
}

watch(
	() => props.error,
	() => props.error && app.$toastErrors(props.error)
)
</script>
